import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { promptVolume, regions as regionsApi } from "../../api/endpoints";

const MONTH_KEYS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

export default function PromptVolume() {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [region, setRegion] = useState("global");
  const [regionOptions, setRegionOptions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);

  async function reloadLog() {
    setLogLoading(true);
    try {
      const r = await promptVolume.log();
      setLog(Array.isArray(r.data?.results) ? r.data.results : []);
    } catch {
      setLog([]);
    } finally {
      setLogLoading(false);
    }
  }

  useEffect(() => {
    reloadLog();
    regionsApi
      .list()
      .then((r) => setRegionOptions(r.data))
      .catch(() => setRegionOptions([]));
  }, []);

  function regionLabel(code) {
    if (!code) return "";
    const localized = t(`regions.${code}`, { defaultValue: "" });
    if (localized) return localized;
    const opt = regionOptions.find((r) => r.value === code);
    return opt?.label || code;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (busy || !keyword.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await promptVolume.lookup(keyword.trim(), region);
      setResult(r.data);
      setLog((prev) => [r.data, ...prev]);
    } catch (err) {
      const data = err?.response?.data;
      setError(data?.detail || t("promptVolume.lookupFailed"));
    } finally {
      setBusy(false);
    }
  }

  const chartData = (result?.ai_monthly_searches || [])
    .slice()
    .reverse()
    .map((row) => ({
      label: `${t(`promptVolume.months.${MONTH_KEYS[(row.month || 1) - 1]}`)} '${String(row.year).slice(2)}`,
      volume: row.ai_search_volume,
    }));

  return (
    <div className="space-y-8 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">{t("promptVolume.title")}</h1>
        <p className="text-sm text-slate-600">{t("promptVolume.subtitle")}</p>
      </div>

      <form onSubmit={onSubmit} className="card space-y-3">
        <textarea
          className="input"
          rows={2}
          placeholder={t("promptVolume.placeholder")}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          maxLength={250}
          required
        />
        <div className="flex items-center gap-3">
          <select
            className="block rounded-md border-slate-300 text-sm focus:border-brand-500 focus:ring-brand-500"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            {regionOptions.length === 0 ? (
              <option value="global">{t("regions.global", "Global")}</option>
            ) : (
              regionOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {regionLabel(r.value)}
                </option>
              ))
            )}
          </select>
          <button className="btn-primary ml-auto" disabled={busy}>
            {busy ? t("promptVolume.checking") : t("promptVolume.checkVolume")}
          </button>
        </div>
        {error && <div className="text-sm text-red-600">{error}</div>}
      </form>

      {result && (
        <section className="card space-y-4">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm text-slate-500">{result.keyword_text}</div>
              <div className="text-3xl font-semibold text-slate-900 mono">
                {result.ai_search_volume.toLocaleString()}
              </div>
              <div className="text-xs text-slate-500">{t("promptVolume.volumeLabel")}</div>
            </div>
          </div>
          {chartData.length > 0 && (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -15 }}>
                <CartesianGrid stroke="#eef2f7" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#94a3b8"
                  fontSize={11}
                  tickLine={false}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="volume" stroke="#4f46e5" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </section>
      )}

      <section className="card p-0 overflow-x-auto">
        <div className="px-4 py-3 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">{t("promptVolume.logTitle")}</h2>
        </div>
        <table className="w-full text-sm min-w-[480px]">
          <thead className="bg-slate-50 text-slate-600 text-left">
            <tr>
              <th className="px-4 py-3 font-medium">{t("promptVolume.colPrompt")}</th>
              <th className="px-4 py-3 font-medium">{t("promptVolume.colVolume")}</th>
              <th className="px-4 py-3 font-medium">{t("promptVolume.colDate")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {logLoading ? (
              <tr>
                <td colSpan="3" className="px-4 py-6 text-slate-500">{t("common.loading")}</td>
              </tr>
            ) : log.length === 0 ? (
              <tr>
                <td colSpan="3" className="px-4 py-6 text-slate-500">{t("promptVolume.noneYet")}</td>
              </tr>
            ) : (
              log.map((row) => (
                <tr key={row.id}>
                  <td className="px-4 py-3 max-w-md">{row.keyword_text}</td>
                  <td className="px-4 py-3 mono">{row.ai_search_volume.toLocaleString()}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {new Date(row.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>
    </div>
  );
}
